import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/components/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

const isoDate = (date) => date.toISOString().split("T")[0];

const getDefaultDates = () => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

  return {
    from: isoDate(oneMonthAgo),
    to: isoDate(today),
  };
};

export function ListadoPedidos() {
  const { user } = useAuth();
  const defaultDates = useMemo(() => getDefaultDates(), []);
  const [fechaDesde, setFechaDesde] = useState(defaultDates.from);
  const [fechaHasta, setFechaHasta] = useState(defaultDates.to);
  const [appliedFilters, setAppliedFilters] = useState(defaultDates);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const cargarPedidos = useCallback(async (filters) => {
    if (!filters?.from || !filters?.to) return;

    setLoading(true);
    setError(null);

    try {
      const fromDate = `${filters.from}T00:00:00`;
      const toDate = `${filters.to}T23:59:59`;

      let query = supabase
        .from("orders")
        .select(
          `
          id,
          number,
          date,
          total,
          cod_rep,
          customer:customers(
            name,
            cif,
            phone,
            movil,
            street,
            city,
            province,
            postalCode
          ),
          shipping_address:shipping_addresses(
            street,
            city,
            province,
            postal_code,
            phone
          ),
          items:order_items(
            quantity
          )
        `
        )
        .gte("created_at", fromDate)
        .lte("created_at", toDate)
        .order("date", { ascending: true });

      if (!user?.ver_comisiones_globales && user?.cod_rep) {
        query = query.eq("cod_rep", user.cod_rep);
      }

      const { data, error: pedidosError } = await query;

      if (pedidosError) throw pedidosError;

      setOrders(data || []);
    } catch (err) {
      console.error("Error al cargar listado de pedidos:", err);
      setError("No se pudieron cargar los pedidos. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    cargarPedidos(appliedFilters);
  }, [appliedFilters, cargarPedidos]);

  const resumen = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        const prendas =
          order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
        return {
          totalImporte: acc.totalImporte + Number(order.total || 0),
          totalPrendas: acc.totalPrendas + prendas,
        };
      },
      { totalImporte: 0, totalPrendas: 0 }
    );
  }, [orders]);

  const formatDate = (value) => {
    try {
      return new Date(value).toLocaleDateString("es-ES");
    } catch (e) {
      return "-";
    }
  };

  const buildAddress = (order) => {
    const addressSource = order.shipping_address || order.customer;
    if (!addressSource) return "-";

    const parts = [
      addressSource.street,
      addressSource.city,
      addressSource.province,
      addressSource.postal_code || addressSource.postalCode,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const getPhone = (order) => {
    return (
      order.customer?.phone ||
      order.customer?.movil ||
      order.shipping_address?.phone ||
      "-"
    );
  };

  const getPrendas = (order) =>
    order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (
      stringValue.includes(";") ||
      stringValue.includes(",") ||
      stringValue.includes("\n") ||
      stringValue.includes('"')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleExportCSV = () => {
    if (!orders.length) return;

    setExporting(true);
    try {
      const headers = [
        "Numero Pedido",
        "Fecha",
        "Cliente",
        "NIF",
        "Direccion",
        "Telefono",
        "Importe",
        "Total Prendas",
      ];

      const rows = orders.map((order) => {
        const data = [
          order.number || "",
          formatDate(order.date),
          order.customer?.name || "",
          order.customer?.cif || "",
          buildAddress(order),
          getPhone(order),
          currencyFormatter.format(Number(order.total || 0)),
          getPrendas(order),
        ];
        return data.map(escapeCSV).join(";");
      });

      const csvContent = [headers.join(";"), ...rows].join("\n");
      const blob = new Blob([`\uFEFF${csvContent}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `listado_pedidos_${appliedFilters.from}_${appliedFilters.to}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exportando CSV:", err);
      setError("No se pudo generar el archivo CSV.");
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = (event) => {
    event?.preventDefault();
    if (!fechaDesde || !fechaHasta) {
      setError("Debes seleccionar una fecha de inicio y fin");
      return;
    }

    setAppliedFilters({ from: fechaDesde, to: fechaHasta });
  };

  return (
    <Card className="p-4 space-y-4">
      <form
        className="grid gap-4 md:grid-cols-[repeat(4,minmax(0,1fr))] lg:grid-cols-[repeat(5,minmax(0,1fr))] items-end"
        onSubmit={handleApplyFilters}
      >
        <div>
          <label className="block text-sm font-medium mb-1">Desde</label>
          <Input
            type="date"
            value={fechaDesde}
            max={fechaHasta}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Hasta</label>
          <Input
            type="date"
            value={fechaHasta}
            min={fechaDesde}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3 flex flex-wrap gap-2">
          <Button type="submit" disabled={loading}>
            {loading ? "Cargando..." : "Aplicar filtros"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={exporting || !orders.length}
            onClick={handleExportCSV}
          >
            {exporting ? "Generando..." : "Descargar CSV"}
          </Button>
        </div>
      </form>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3">
          <p className="text-sm text-gray-500">Pedidos</p>
          <p className="text-2xl font-semibold">{orders.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-sm text-gray-500">Importe total</p>
          <p className="text-2xl font-semibold">
            {currencyFormatter.format(resumen.totalImporte)}
          </p>
        </Card>
        <Card className="p-3">
          <p className="text-sm text-gray-500">Total prendas</p>
          <p className="text-2xl font-semibold">
            {resumen.totalPrendas.toLocaleString("es-ES")}
          </p>
        </Card>
        <Card className="p-3">
          <p className="text-sm text-gray-500">Rango aplicado</p>
          <p className="text-sm font-medium">
            {appliedFilters.from} → {appliedFilters.to}
          </p>
        </Card>
      </div>

      <div className="border rounded-md">
        <div className="overflow-auto max-h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nº Pedido</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead className="text-right">Importe</TableHead>
                <TableHead className="text-right">Prendas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    No hay pedidos en el rango seleccionado
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.number}</TableCell>
                    <TableCell>{formatDate(order.date)}</TableCell>
                    <TableCell>{order.customer?.name || "-"}</TableCell>
                    <TableCell>{order.customer?.cif || "-"}</TableCell>
                    <TableCell>{buildAddress(order)}</TableCell>
                    <TableCell>{getPhone(order)}</TableCell>
                    <TableCell className="text-right">
                      {currencyFormatter.format(Number(order.total || 0))}
                    </TableCell>
                    <TableCell className="text-right">
                      {getPrendas(order)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Card>
  );
}
